import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Skeleton,
  UserGroupBadge,
  VStack,
} from '@frontend/components';

import { getActiveGroup } from 'store/session/selectors';

import { useUserGroupCollectionContext } from 'utils/multisite/UserGroupCollectionProvider';

export const CreateQuoteButton = () => {
  const { availableGroups, userGroupsById, initChangeSite } =
    useUserGroupCollectionContext();
  const activeGroup = useSelector(getActiveGroup);

  const nonActiveGroups = useMemo(() => {
    return (
      availableGroups.filter((group) => group.id !== activeGroup?.id) ?? []
    );
  }, [availableGroups, activeGroup]);

  const onSelect = useCallback(
    (groupId: number) => {
      initChangeSite({
        nextUrl: '/quotes/create',
        userGroupId: groupId,
        skipConfirmation: true,
      });
    },
    [initChangeSite],
  );

  return (
    <Menu>
      <Skeleton isLoaded={!!activeGroup && availableGroups.length > 0}>
        <MenuButton as={Button} variant="primary">
          Create Quote
        </MenuButton>
      </Skeleton>
      <MenuList maxWidth={48}>
        {activeGroup && (
          <>
            <MenuGroup title="Active Site">
              <MenuItem onClick={() => onSelect(activeGroup.id)}>
                <UserGroupBadge
                  maxWidth="full"
                  name={userGroupsById[activeGroup?.id]?.businessName}
                  colorPalette={userGroupsById[activeGroup.id]?.color}
                  icon={userGroupsById[activeGroup.id]?.icon}
                />
              </MenuItem>
            </MenuGroup>
            <MenuDivider />
          </>
        )}
        <MenuGroup title="Other Sites">
          {nonActiveGroups.map((group) => (
            <MenuItem key={group.id} onClick={() => onSelect(group.id)}>
              <UserGroupBadge
                maxWidth="full"
                name={group.businessName}
                colorPalette={group.color}
                icon={group.icon}
              />
            </MenuItem>
          ))}
        </MenuGroup>
        <Alert
          mt={0.5}
          status="warning"
          size="sm"
          variant="borderless"
          minWidth="100%"
          maxWidth={36}
        >
          <AlertIcon />
          <VStack spacing={0} align="start">
            <AlertTitle>Site selection will switch your active site</AlertTitle>
            <AlertDescription>
              Unsaved changes in open tabs will be lost when switching active
              sites.
            </AlertDescription>
          </VStack>
        </Alert>
      </MenuList>
    </Menu>
  );
};
